// externals
import type { GenericObject, IPencilOfferInputs, PaymentTerm } from '@makemydeal/dr-platform-types';
import {
    BALLOON,
    CashOptions,
    FINANCE,
    LEASE,
    RequestType,
    RESIDUAL_PERCENT,
    SpecialPaymentFormula
} from '@makemydeal/dr-platform-types';
// Import directly from specific files to break circular dependency with selectors/index.ts
import { getTermsByIdPayment } from '../selectors/offerSelectors';
import { getPaymentSpecialFormula } from '../selectors/paymentSelectors';
import { getFlatTaxItem, getMappedTermId, getTermId } from '../selectors/utils/nonSelectorUtils';
import type { NonCashOfferType } from '../types/commonTypes';
import { toSplitPercent } from '../utils/reserveSplitUtils';
import type { OfferPayment, OfferTermOffer, PreferredTerms } from '../types/offerTypes';
import { getIsPreferredTermField, parseSelectedTermMonths } from '../utils/paymentUtils';
import { translateApplyMethodsToEquityAllocationMethod } from '../utils/equityAllocationUtils';
import { isImplausibleApr, isImplausibleMoneyFactor, resolveTermRateType, termHasImplausibleMoneyFactor } from '../utils/rateUtils';

const applyTermRateOverrides = (node: GenericObject, paymentTerm: PaymentTerm, offerType: NonCashOfferType): void => {
    const rateTypeOverride = resolveTermRateType(paymentTerm, offerType);
    if (!rateTypeOverride) return;

    node.sellRateOverride = paymentTerm.sellRate;
    node.buyRateOverride = paymentTerm.buyRate;
    node.rateTypeOverride = rateTypeOverride;
};

export const getPreferredTerm = (offerType: NonCashOfferType, terms: PreferredTerms): string | undefined => {
    const termMonths = getIsPreferredTermField(offerType, terms, 'months');
    const downPayment = getIsPreferredTermField(offerType, terms, 'requestedDownPayment');

    return getTermId(offerType, termMonths === null ? undefined : termMonths, downPayment === null ? undefined : downPayment);
};

export const getBaseOfferTerm = (offer: OfferTermOffer, offerType: NonCashOfferType): string => {
    const terms = offer?.terms;
    const selectedTermMonth = offer[offerType]?.selectedTermMonths;
    const mappedTermId = getMappedTermId(offerType, selectedTermMonth);
    const mappedTerms = mappedTermId ? terms?.mappings?.[mappedTermId] : undefined;

    const selectedTerm = offerType === LEASE ? offer.selectedLeaseTerm : offer.selectedFinanceTerm;
    const currentTerm = mappedTerms || selectedTerm;

    const parsedTermMonth = parseSelectedTermMonths(currentTerm);

    return getTermId(offerType, parsedTermMonth!) as string;
};

export const getOfferTerm = (offer: OfferTermOffer, offerType: NonCashOfferType, downPayment?: number): string => {
    const terms = offer?.terms;
    const selectedTermMonth = offer?.[offerType]?.selectedTermMonths;
    const mappedTermId = getMappedTermId(offerType, selectedTermMonth);
    const mappedTerms = mappedTermId ? terms?.mappings?.[mappedTermId] : undefined;

    const selectedTerm = offerType === LEASE ? offer.selectedLeaseTerm : offer.selectedFinanceTerm;
    const currentTerm = mappedTerms || selectedTerm;

    const parsedTermMonth = parseSelectedTermMonths(currentTerm)!;
    const termData = offer?.terms?.byId?.[getTermId(offerType, parsedTermMonth) as string];

    const isFordFlexBuy = offer?.payment?.specialPaymentFormula === SpecialPaymentFormula.FordFlexBuy;
    const initialPaymentDiscount = isFordFlexBuy ? offer?.payment?.initialPaymentDiscount : undefined;

    const extension = {
        ...(termData?.requestedDownPayment || downPayment ? { DOWNPAYMENT: termData?.requestedDownPayment || downPayment } : {}),
        ...(initialPaymentDiscount ? { FORDFLEXBUY: initialPaymentDiscount } : {})
    };

    const termId = getTermId(offerType, parsedTermMonth, extension);

    return termId || getPreferredTerm(offerType, terms)!;
};

export const getDefaultTermId = (offer: OfferTermOffer, offerType: NonCashOfferType): string | undefined => {
    const payment = offer.payment || ({} as OfferPayment);
    const offerTypeTermsKey = `${offerType}Terms`;
    const offerTypeUppercase = offerType.toUpperCase();
    const normalizedOfferType = RequestType[offerTypeUppercase as keyof typeof RequestType];
    const selectedTermProperty = `selected${normalizedOfferType}Term`;

    if (offer[selectedTermProperty as keyof OfferTermOffer]) {
        return offer[selectedTermProperty as keyof OfferTermOffer] as string | undefined;
    }

    const termsValue = offer[offerTypeTermsKey as keyof OfferTermOffer];
    if (termsValue && typeof termsValue === 'object' && 'term' in termsValue && typeof termsValue.term === 'number') {
        return getTermId(offerType, termsValue.term);
    }

    const preferredTerm = getIsPreferredTermField(offerType, offer.terms, 'months');

    if (preferredTerm) {
        return getTermId(offerType, preferredTerm);
    }

    return offer.type === offerType ? payment.id : undefined;
};

export const getSavedSellRateOverrideUnit = (
    offer: OfferTermOffer,
    offerType: NonCashOfferType
): PaymentTerm['rateType'] | undefined => {
    const paymentTerm = getTermsByIdPayment(offer, getDefaultTermId(offer, offerType));
    if (!paymentTerm?.rateType || paymentTerm.months == null) return undefined;
    const savedOverride = (offer[offerType] as GenericObject | undefined)?.overridesByTerm?.[paymentTerm.months];
    const savedSellRate = savedOverride?.sellRate;
    if (savedSellRate == null) return undefined;
    if (savedOverride.rateType && savedOverride.rateType !== paymentTerm.rateType) return undefined;
    if (termHasImplausibleMoneyFactor(paymentTerm, offerType)) return undefined;
    if (isImplausibleMoneyFactor(paymentTerm.rateType, savedSellRate) || isImplausibleApr(paymentTerm.rateType, savedSellRate)) {
        return undefined;
    }
    return paymentTerm.rateType;
};

export const buildCashOfferNode = (cash: any) => ({
    appliedIncentiveAmount: cash?.appliedIncentiveAmount || 0,
    appliedIncentiveIds: cash?.appliedIncentiveIds || [],
    appliedIncentives: cash?.appliedIncentives || [],
    dealerFees: cash?.dealerFees,
    dealerFeesTotal: cash?.dealerFeesTotal,
    discount: cash?.discount,
    dmvFees: cash?.dmvFees || 0,
    fees: cash?.fees,
    incentives: cash?.incentives || 0,
    initialOfferPrice: cash?.initialOfferPrice,
    isUpdatedByUser: false,
    manualIncentives: cash?.manualIncentives,
    offerPrice: cash?.offerPrice,
    ...(cash?.profit != null && { profit: cash?.profit }),
    ...(cash?.psRawData && { psRawData: cash?.psRawData }),
    retailPriceOverride: cash?.retailPriceOverride,
    paymentGridData: {
        paymentGridToggle: false
    },
    priceWithAdds: cash?.priceWithAdds || 0,
    selectedIncentives: cash?.selectedIncentives,
    taxBreakdown: cash?.taxBreakdown,
    taxItems: cash?.taxItems,
    totalAmountDueAfterCredits: cash?.totalAmountDueAfterCredits || 0,
    totalAmountDueWithoutProducts: cash?.totalAmountDueWithoutProducts || 0,
    totalIncentives: cash?.totalIncentives || 0,
    totalFee: cash?.totalFee || 0,
    totalTax: cash?.totalTax || 0,
    totalTaxReduction: cash?.totalTaxReduction,
    tradeIn: cash?.tradeIn,
    dealDeposit: cash?.dealDeposit || 0,
    lienHolder: cash?.lienHolder || {},
    netCashPayout: cash?.netCashPayout || 0,
    registrationType: cash?.registrationType,
    registrationExpiresOn: cash?.registrationExpiresOn
});

// DE720579 (gated by overrideTaxFixes_US1984109): On reload (notably SV) the saved deal can omit the
// FLAT tax item even though a manual total-tax override was applied. In that case getFlatTaxItem
// returns undefined and manualTotalTax was lost, making hasManualTotalTax false → includeTaxes stayed
// true and the override was recomputed away / never sent in the payload. Recover it from the
// persisted offer-node value, then from the term's advanceFlatTax (the upfront flat-tax amount).
const resolveManualTotalTax = (
    paymentTerm: PaymentTerm,
    offerNode: { manualTotalTax?: number },
    enableOverrideTaxFixes?: boolean
): number | undefined => {
    const flatTaxAmount = getFlatTaxItem(paymentTerm.taxItems)?.taxAmount;
    if (flatTaxAmount != null || !enableOverrideTaxFixes) {
        return flatTaxAmount;
    }

    if (offerNode?.manualTotalTax != null) {
        return offerNode.manualTotalTax;
    }

    const advanceFlatTax = (paymentTerm as { advanceFlatTax?: number }).advanceFlatTax;
    return advanceFlatTax != null && advanceFlatTax > 0 ? advanceFlatTax : undefined;
};

/**
 * Restores the reserve split on a reloaded deal.
 *
 * The saved deal carries the split as `proceedDetails.reservePaid`, which is the only reserve field
 * that survives the whole round trip: MV writes it into the AIO proceeds node, DXG stores it on
 * `FinanceReserveDetail`, and acceleron maps it back onto the term's `profit`. Nothing persists
 * *whether a Finance Manager set it by hand* - neither `isManualReservePaid` nor an input
 * `reserveSplit` exists in the Deal API - so a reloaded deal is seeded from the value it was saved
 * with and keeps it.
 *
 * The consequence is deliberate: a reloaded deal re-sends its split, so Payment Services no longer
 * re-resolves the dealer's DRaaS Reserve Setup for it. Clearing the field is the way back - that
 * sends `undefined`, the orchestrator omits `userProgram.reserve`, and the lookup runs again.
 *
 * `reservePaid` is a fraction (0.56) while the override is a whole-number percent (56), hence the
 * conversion.
 */
const resolveReserveSplitOverride = (paymentTerm: PaymentTerm) =>
    toSplitPercent((paymentTerm as { profit?: { reservePaid?: number } }).profit?.reservePaid);

export const buildFinanceOfferNode = (
    offer: OfferTermOffer,
    creditTier: string,
    isUserProgramQuotes?: boolean,
    enableOverrideTaxFixes?: boolean
): IPencilOfferInputs => {
    const finance = offer.finance || {};
    const defaultTermId = getDefaultTermId(offer, FINANCE);
    const paymentTerm = getTermsByIdPayment(offer, defaultTermId) || ({} as PaymentTerm);
    const manualTotalTax = resolveManualTotalTax(paymentTerm, finance as { manualTotalTax?: number }, enableOverrideTaxFixes);

    const financeNode: IPencilOfferInputs = {
        reserveSplitOverride: resolveReserveSplitOverride(paymentTerm),
        // TODO Delete creditDecisionLenderName after feature toggle enableAppliedLenderInfo has been deleted
        creditDecisionEnabled: finance.creditDecisionEnabled,
        creditDecisionLender: finance.creditDecisionLender,
        creditDecisionLenderName: finance.creditDecisionLenderName,
        titlingLender: finance.titlingLender,
        creditDecisionStatus: finance.creditDecisionStatus,
        creditTier,
        deferredDownPayment: finance.deferredDownPayment,
        downPayment: finance.downPayment,
        incentiveTotalOverride: finance.incentiveTotalOverride,
        initialOfferPrice: finance.initialOfferPrice,
        isUpdatedByUser: false,
        manualIncentives: finance.manualIncentives,
        manualTotalTax,
        offerPrice: finance.offerPrice,
        netSellingPrice: finance.netSellingPrice,
        paymentGridData: {
            paymentGridToggle: false
        },
        gridHeadersData: { terms: undefined, downPayments: undefined },
        retailPriceOverride: finance.retailPriceOverride,
        selectedIncentives: finance.selectedIncentives,
        specialPaymentFormula: getPaymentSpecialFormula(offer) as SpecialPaymentFormula.FordFlexBuy,
        term: defaultTermId,
        totalIncentives: finance.totalIncentives || 0,
        taxItems: paymentTerm.taxItems || [],
        tradeIn: finance?.tradeIn,
        registrationType: finance.registrationType,
        registrationExpiresOn: finance.registrationExpiresOn
    };

    if (isUserProgramQuotes) {
        applyTermRateOverrides(financeNode, paymentTerm, FINANCE);
    } else {
        financeNode.overridesByTerm = finance?.overridesByTerm;
    }

    return financeNode;
};

export const buildLeaseOfferNode = (
    offer: OfferTermOffer,
    creditTier: string,
    isUserProgramQuotes?: boolean,
    enableOverrideTaxFixes?: boolean,
    isEquityAllocationOrderEnabled?: boolean
): IPencilOfferInputs => {
    const lease = offer.lease || {};
    const defaultTermId = getDefaultTermId(offer, LEASE);
    const paymentTerm = getTermsByIdPayment(offer, defaultTermId) || ({} as PaymentTerm);
    const manualTotalTax = resolveManualTotalTax(paymentTerm, lease as { manualTotalTax?: number }, enableOverrideTaxFixes);

    const leaseNode: any = {
        reserveSplitOverride: resolveReserveSplitOverride(paymentTerm),
        // TODO Delete creditDecisionLenderName after feature toggle enableAppliedLenderInfo has been deleted
        acqFeeOverride: lease.acqFeeOverride,
        acqFeeUpFrontOverride: lease.acqFeeUpFrontOverride,
        annualMiles: lease.selectedAnnualMiles,
        capitalizeFirstPayment: paymentTerm.firstPaymentCapitalized,
        cashOffsetAmountAddedToPrice: paymentTerm.cashOffsetAmountAddedToPrice,
        creditDecisionEnabled: lease.creditDecisionEnabled,
        creditDecisionLender: lease.creditDecisionLender,
        creditDecisionLenderName: lease.creditDecisionLenderName,
        titlingLender: lease.titlingLender,
        creditDecisionStatus: lease.creditDecisionStatus,
        creditTier: creditTier,
        downPayment: lease.downPayment,
        incentiveTotalOverride: lease.incentiveTotalOverride,
        initialOfferPrice: lease.initialOfferPrice,
        isTargetOutOfPocket: offer.cashApplyMethod === CashOptions.AddCashOffsetToPrice,
        isUpdatedByUser: false,
        manualIncentives: lease.manualIncentives,
        manualTotalTax,
        mileageChargeOverride: lease.mileageChargeOverride,
        offerPrice: lease.offerPrice,
        netSellingPrice: lease.netSellingPrice,
        paymentGridData: {
            paymentGridToggle: false
        },
        gridHeadersData: { terms: undefined, downPayments: undefined },
        overridesByTerm: lease.overridesByTerm,
        retailPriceOverride: lease.retailPriceOverride,
        securityDepositOverride: lease.securityDepositOverride,
        selectedIncentives: lease.selectedIncentives,
        sellRate: lease.sellRate,
        term: defaultTermId,
        totalIncentives: lease?.totalIncentives || 0,
        taxItems: paymentTerm.taxItems || [],
        tradeIn: lease.tradeIn,
        registrationType: lease.registrationType,
        registrationExpiresOn: lease.registrationExpiresOn,
        equityAllocationMethod: lease.equityAllocationMethod,
        tradeApplyMethod: lease.tradeApplyMethod,
        rebateApplyMethod: lease.rebateApplyMethod,
        dealerCashApplyMethod: lease.dealerCashApplyMethod,
        cashApplyMethod: lease.cashApplyMethod,
        secondPaymentDueDate: paymentTerm.secondPaymentDueDate,
        retroMiles: paymentTerm.retroMilesThreshold,
        standardAllowedMiles: paymentTerm.standardMiles,
        thresholdMiles: paymentTerm.initialMilesThreshold,
        inceptionRate: paymentTerm.initialMileageCharge,
        totalExcessMilesPurchased: paymentTerm.extraMiles,
        totalMiles: paymentTerm.totalMiles,
        excessMileageCharge: paymentTerm.excessMileageCharge,
        excessMileRate: paymentTerm.extraCostPerMile,
        retroMilesThreshold: paymentTerm.retroMilesThreshold,
        initialMilesThreshold: paymentTerm.initialMilesThreshold,
        standardMiles: paymentTerm.standardMiles
    };
    if (isUserProgramQuotes) {
        applyTermRateOverrides(leaseNode, paymentTerm, LEASE);

        leaseNode.baseResidualTypeOverride = RESIDUAL_PERCENT;
        leaseNode.adjustedResidualTypeOverride = RESIDUAL_PERCENT;

        leaseNode.baseResidualAmountOverride = paymentTerm.baseResidualDollar;
        leaseNode.baseResidualPercentOverride = paymentTerm.baseResidualPercent;
        leaseNode.programResidualPercentOverride = (paymentTerm as GenericObject).programResidualPercent;
        leaseNode.programResidualOverride = (paymentTerm as GenericObject).programResidual;
        leaseNode.adjustedResidualAmountOverride = paymentTerm.adjustedResidualValue;
        leaseNode.adjustedResidualPercentOverride = paymentTerm.adjustedResidualPercent;
    }
    if (offer.dealXgDetails?.honorOverrides && typeof paymentTerm.isTaxCapped === 'boolean') {
        leaseNode.taxOverrideUpfront = !paymentTerm.isTaxCapped;
    }

    // On deal load, derive the equity allocation order from a legacy deal's apply methods when it has none.
    if (isEquityAllocationOrderEnabled && !leaseNode.equityAllocationMethod) {
        leaseNode.equityAllocationMethod = translateApplyMethodsToEquityAllocationMethod(
            lease.cashApplyMethod ?? offer.cashApplyMethod,
            lease.tradeApplyMethod,
            lease.rebateApplyMethod
        );
    }

    return leaseNode;
};

export const buildBalloonOfferNode = (
    offer: OfferTermOffer,
    creditTier: string,
    isUserProgramQuotes?: boolean,
    enableOverrideTaxFixes?: boolean
): IPencilOfferInputs => {
    const balloon = offer.balloon || {};
    const defaultTermId = getDefaultTermId(offer, BALLOON);
    const paymentTerm = getTermsByIdPayment(offer, defaultTermId) || ({} as PaymentTerm);
    const manualTotalTax = resolveManualTotalTax(paymentTerm, balloon as { manualTotalTax?: number }, enableOverrideTaxFixes);

    const balloonNode: any = {
        reserveSplitOverride: resolveReserveSplitOverride(paymentTerm),
        annualMiles: balloon.selectedAnnualMiles,
        balloonTerm: paymentTerm.balloonTerm,
        balloonType: paymentTerm.balloonType,
        capitalizeFirstPayment: paymentTerm.firstPaymentCapitalized,
        cashOffsetAmountAddedToPrice: paymentTerm.cashOffsetAmountAddedToPrice,
        creditDecisionEnabled: balloon.creditDecisionEnabled,
        creditDecisionLender: balloon.creditDecisionLender,
        creditDecisionLenderName: balloon.creditDecisionLenderName,
        titlingLender: balloon.titlingLender,
        creditDecisionStatus: balloon.creditDecisionStatus,
        creditTier: creditTier,
        downPayment: balloon.downPayment,
        incentiveTotalOverride: balloon.incentiveTotalOverride,
        initialOfferPrice: balloon.initialOfferPrice,
        isUpdatedByUser: false,
        manualIncentives: balloon.manualIncentives,
        manualTotalTax,
        offerPrice: balloon.offerPrice,
        netSellingPrice: balloon.netSellingPrice,
        paymentGridData: {
            paymentGridToggle: false
        },
        gridHeadersData: { terms: undefined, downPayments: undefined },
        overridesByTerm: balloon.overridesByTerm,
        retailPriceOverride: balloon.retailPriceOverride,
        securityDepositOverride: balloon.securityDepositOverride,
        selectedIncentives: balloon.selectedIncentives,
        sellRate: balloon.sellRate,
        term: defaultTermId,
        totalIncentives: balloon?.totalIncentives || 0,
        taxItems: paymentTerm.taxItems || [],
        tradeIn: balloon.tradeIn,
        registrationType: balloon.registrationType,
        registrationExpiresOn: balloon.registrationExpiresOn,
        equityAllocationMethod: balloon.equityAllocationMethod,
        tradeApplyMethod: balloon.tradeApplyMethod,
        rebateApplyMethod: balloon.rebateApplyMethod,
        dealerCashApplyMethod: balloon.dealerCashApplyMethod
    };
    if (isUserProgramQuotes) {
        applyTermRateOverrides(balloonNode, paymentTerm, BALLOON);

        balloonNode.baseResidualTypeOverride = RESIDUAL_PERCENT;
        balloonNode.adjustedResidualTypeOverride = RESIDUAL_PERCENT;

        balloonNode.baseResidualAmountOverride = paymentTerm.baseResidualDollar;
        balloonNode.baseResidualPercentOverride = paymentTerm.baseResidualPercent;
        balloonNode.programResidualPercentOverride = paymentTerm.programResidualPercent;
        balloonNode.programResidualOverride = paymentTerm.programResidual;
        balloonNode.adjustedResidualAmountOverride = paymentTerm.adjustedResidualValue;
        balloonNode.adjustedResidualPercentOverride = paymentTerm.adjustedResidualPercent;
        balloonNode.balloonPaymentAmountOverride = paymentTerm.baseResidualDollar;
    } else {
        balloonNode.overridesByTerm = balloon?.overridesByTerm;
    }
    return balloonNode;
};
type IncentiveRecords = { byId?: Record<string, { manual?: boolean } | undefined>; allIds?: unknown[] };
type IncentiveRowLists = { conditionals?: Array<{ id?: unknown }>; nonConditionals?: Array<{ id?: unknown }> };

const collectRepeatedKeys = (keys: unknown[], collapsed: Set<string>): void => {
    const seen = new Set<string>();
    keys.forEach((id) => {
        const key = String(id);
        if (seen.has(key)) {
            collapsed.add(key);
            return;
        }
        seen.add(key);
    });
};

const getRowKeys = (rows: IncentiveRowLists | undefined): unknown[] => [
    ...(rows?.conditionals ?? []).map((row) => row?.id),
    ...(rows?.nonConditionals ?? []).map((row) => row?.id)
];

// A saved deal lists one entry per applied row, so several rows can share an id while byId keeps only
// the last of them. Those records cannot speak for the id. The amount bearing rows keep every entry
// even once the identity lists are deduped, so they are the signal that survives.
const getCollapsedIncentiveIds = (incentives: IncentiveRecords | undefined, rows: IncentiveRowLists | undefined): Set<string> => {
    const collapsed = new Set<string>();
    collectRepeatedKeys(incentives?.allIds ?? [], collapsed);
    collectRepeatedKeys(getRowKeys(rows), collapsed);
    return collapsed;
};

const isCustomIncentiveRecord = (
    incentives: IncentiveRecords | undefined,
    incentiveId: string,
    collapsedIds: Set<string>
): boolean => incentives?.byId?.[incentiveId]?.manual === true && !collapsedIds.has(incentiveId);

// A converted incentive persists under the same id as the program incentive it came from, so on load
// its id reads as a selected program row. The stored record's `manual` flag is the only thing that
// tells them apart — drop those ids so the program row does not come back checked next to the custom.
export const dropCustomIncentiveIds = <T>(
    selectedIncentives: T,
    incentives: IncentiveRecords | undefined,
    appliedRows?: IncentiveRowLists
): T => {
    if (!Array.isArray(selectedIncentives)) {
        return selectedIncentives;
    }
    const collapsedIds = getCollapsedIncentiveIds(incentives, appliedRows);
    const kept = selectedIncentives.filter(
        (incentiveId) => !isCustomIncentiveRecord(incentives, String(incentiveId), collapsedIds)
    );
    return Array.from(new Set(kept)) as unknown as T;
};

export const isNumber = (value: unknown): value is number => typeof value === 'number';

export const isUndefined = (value: unknown): value is undefined => typeof value === 'undefined';
